import React from 'react';
import { Link, graphql } from 'gatsby';
import {
  Layout,
  SliceZone,
} from '../components';
import {
  Wrapper,
} from '../components/common';
import {
  ProductListItem,
} from '../components/shop';
import {
  PostList,
} from '../components/blog';
import {
  useMousePosition,
  useDimensions,
  useScrollPosition,
} from '../util/hooks';
import './blog-post.scss';

const Post = (props) => {
  const {
    data: {
      site: {
        siteMetadata,
      },
      post,
      relatedPosts,
    },
    location,
  } = props;
  const { data } = post;
  const {
    blogSlug,
    shopSlug,
  } = siteMetadata;
  const {
    postTitle,
    date,
    author,
    body,
    category,
    metaTitle,
    metaDescription,
    openGraphImage,
    featuredProducts,
  } = data;
  const {
    x: cursorX,
    y: cursorY,
  } = useMousePosition();
  const {
    viewportWidth,
    viewportHeight,
    documentHeight,
  } = useDimensions();
  const viewportTopPos = useScrollPosition();
  const pathPrefix = `/${blogSlug}`;
  const categoryData = category.document[0].data || {};
  const categorySlug = category.document[0].uid || null;
  const {
    categoryName = null,
  } = categoryData;
  const seoData = {
    metaTitle,
    metaDescription,
    openGraphImage,
  };
  const authorData = author.document[0].data || {};
  const { authorName = null } = authorData;
  const relatedPostItems = relatedPosts && relatedPosts.edges.map(relatedPost => relatedPost.node);
  return (
    <Layout
      location={location}
      seoData={seoData}
      cursorX={cursorX}
      cursorY={cursorY}
      viewportWidth={viewportWidth}
      viewportHeight={viewportHeight}
      documentHeight={documentHeight}
      viewportTopPos={viewportTopPos}
    >
      <article className="blog-post">
        <div className="post-intro">
          <Wrapper isBound>
            <Link
              to={`/${blogSlug}/${categorySlug}/`}
              className="back-to-category-link styled-link"
              aria-label={`Return to all posts in ${categoryName.text}`}
            >
              <span>{`Back to ${categoryName.text}`}</span>
            </Link>
            <h1 className="post-title">{postTitle.text}</h1>
            <div className="post-meta">
              <span className="post-date">{date}</span>
              { authorName && (
                <span className="post-author">{`Written by ${authorName.text}`}</span>
              )}
            </div>
          </Wrapper>
        </div>
        <section className="post-body">
          <SliceZone
            allSlices={body}
            location={location}
          />
        </section>
        {featuredProducts && featuredProducts.length > 0 && (
          <div className="post-featured-products">
            <Wrapper isBound>
              <h2 className="title">Used in this project</h2>
              <div className="products">
                {featuredProducts.map((productItem) => {
                  const { product } = productItem;
                  if (!product || !product.handle) return null;
                  return (
                    <ProductListItem
                      key={product.handle}
                      shopSlug={shopSlug}
                      {...product}
                    />
                  );
                })}
              </div>
            </Wrapper>
          </div>
        )}
        <div className="post-related-posts">
          <Wrapper isBound>
            <h2 className="title">More like this one</h2>
            <PostList
              posts={relatedPostItems}
              siteMetadata={siteMetadata}
              pathPrefix={pathPrefix}
            />
          </Wrapper>
        </div>
      </article>
    </Layout>
  );
};

export default Post;


export const postQuery = graphql`
query PostBySlug($uid: String!) {
  site {
    siteMetadata {
      blogSlug
      shopSlug
    }
  },
  post: prismicBlogPost(uid: { eq: $uid }) {
    uid
    data {
      metaTitle: meta_title {
        html
        text
      },
      metaDescription: meta_description {
        html
        text
      },
      openGraphImage: open_graph_image {
        alt
        copyright
        url
      }
      postTitle: post_title {
        text
      }
      date(formatString: "DD / MM / YYYY")
      category {
        document {
          uid
          data {
            categoryName: category_name {
              text
            }
          }
        }
      }
      author {
        document {
          data {
            authorName: author_name {
              text
            }
          }
        }
      }
      # featuredProducts: featured_products {
      #   product {
      #     id
      #     title
      #     handle
      #     variants {
      #       price
      #     }
      #     images {
      #       id
      #       url: src
      #     }
      #   }
      # }
      body {
        # ... on PrismicBlogPostBodyFeaturedText {
        #   id
        #   sliceType: slice_type
        #   primary {
        #     textBody: text_body {
        #       html
        #     }
        #   }
        # }
        # ... on PrismicBlogPostBodyPostVideo {
        #   id
        #   sliceType: slice_type
        #   primary {
        #     videoProvider: video_provider
        #     videoUrl: video_url {
        #       url
        #     }
        #     caption {
        #       text
        #     }
        #   }
        # }
        ... on PrismicBlogPostBodyRichText {
          id
          sliceType: slice_type
          primary {
            textBody: text_body {
              html
            }
          }
        }
        ... on PrismicBlogPostBodyPostImage {
          id
          sliceType: slice_type
          primary {
            image {
              localFile {
                childImageSharp {
                  fluid(maxWidth: 1200, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
              alt
            }
            imageStyle: image_style
            # imageCaption: image_caption {
            #   text
            # }
          }
        }
        ... on PrismicBlogPostBodyFeaturedList {
          id
          sliceType: slice_type
          primary {
            title {
              text
            }
          }
          items {
            listItem: list_item {
              html
            }
          }
        }
        ... on PrismicBlogPostBodyPostImageSet {
          id
          sliceType: slice_type
          items {
            image {
              alt
              localFile {
                childImageSharp {
                  fluid(maxWidth: 800, quality: 90) {
                    ...GatsbyImageSharpFluid_withWebp
                  }
                }
              }
              url
            }
            imageCaption: image_caption {
              text
            }
          }
          primary {
            imageStyle: image_style
          }
        }

      }
    }
  }
  relatedPosts: allPrismicBlogPost(
    limit: 3,
    sort: { fields: [data___date], order: DESC }
    filter: { uid: { ne: $uid } }
  ) {
    edges {
      node {
        uid,
        data {
          metaDescription: meta_description {
            html
            text
          },
          postTitle: post_title {
            text
          }
          featuredImage: featured_image {
            alt
            localFile {
              childImageSharp {
                fluid(maxWidth: 800, quality: 90) {
                  ...GatsbyImageSharpFluid_withWebp
                }
              }
            }
          }
        }
      }
    }
  }
}
`;
